import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../../Topbar';
import { SearchIcon, ClockIcon, EditIcon, DbIcon, ApiIcon, ShieldIcon, CodeIcon, LayersIcon } from '../theme';
import { useTheme } from '../../ThemeContext';
import { useOutletContext } from 'react-router-dom';
import api from '../../../services/api'; // Instance Axios configurée
import useResponsive from '../ResponsiveHook'; 

// Données de simulation locales (Fallback)
const DEFAULT_COMPANY_TESTS = [
  {
    id: 1,
    company_name: "TechCorp Inc.",
    job_name: "Full Stack Development Challenge",
    description: "Build a task app with a React frontend and Node.js backend. Must support CRUD, auth, and real-time updates.",
    skills: [{ name: "React" }, { name: "Node.js" }, { name: "MongoDB" }],
    test_duration_min: 90,
    xp: "+500 XP", 
    experience_level: "HARD",
    icon: <CodeIcon />,
    color: "#3B82F6"
  },
  {
    id: 2,
    company_name: "TechCorp Inc.",
    job_name: "Senior Frontend Developer",
    description: "Implement a high-performance custom hook 'useCachedFetch' to optimize heavy analytics query re-renders.",
    skills: [{ name: "React" }, { name: "TypeScript" }, { name: "CSS" }],
    test_duration_min: 120,
    xp: "+500 XP",
    experience_level: "SENIOR",
    icon: <CodeIcon />,
    color: "#A855F7"
  },
  {
    id: 3,
    company_name: "CloudScale",
    job_name: "Cloud Architecture Design",
    description: "Design a scalable architecture for a web platform handling 1M+ daily requests using AWS services.",
    skills: [{ name: "AWS" }, { name: "Docker" }, { name: "Architecture" }],
    test_duration_min: 75,
    xp: "+450 XP",
    experience_level: "HARD",
    icon: <LayersIcon />,
    color: "#60A5FA"
  },
  {
    id: 4,
    company_name: "Finova Labs",
    job_name: "API Integration Challenge",
    description: "Build and document a public REST API returning structured JSON data with authentication and rate limiting.",
    skills: [{ name: "REST API" }, { name: "JavaScript" }, { name: "JSON" }],
    test_duration_min: 45,
    xp: "+280 XP",
    experience_level: "MID",
    icon: <ApiIcon />,
    color: "#3B82F6"
  },
  {
    id: 5,
    company_name: "SecureOps",
    job_name: "Cybersecurity Vulnerability Challenge",
    description: "Identify XSS and SQL injection vulnerabilities in a login system and implement fixes using proper session handling.",
    skills: [{ name: "Security" }, { name: "Authentication" }, { name: "Node" }],
    test_duration_min: 80,
    xp: "+520 XP",
    experience_level: "HARD",
    icon: <ShieldIcon />,
    color: "#A855F7"
  },
  {
    id: 6,
    company_name: "PlayForge Studios",
    job_name: "Game Logic Development Challenge",
    description: "Implement the core game logic for a small browser game such as Snake or Tic-Tac-Toe.",
    skills: [{ name: "JavaScript" }, { name: "Logic" }, { name: "Canvas" }],
    test_duration_min: 60,
    xp: "+280 XP",
    experience_level: "MID",
    icon: <CodeIcon />,
    color: "#60A5FA"
  }
];

const DEFAULT_PLATFORM_TESTS = [
  {
    id: 7,
    job_name: "Authentication Implementation Challenge",
    description: "Implement a secure authentication system using JWT tokens. Build register, login, and protected resource endpoints.",
    skills: [{ name: "Security" }, { name: "Authentication" }, { name: "JWT" }],
    test_duration_min: 50,
    xp: "+240 XP",
    experience_level: "MID",
    icon: <ShieldIcon />,
  },
  {
    id: 8,
    job_name: "Git Workflow Challenge",
    description: "Demonstrate proper git branching strategies, commit conventions, and maintaining a clean commit history.",
    skills: [{ name: "Git" }, { name: "Version Control" }],
    test_duration_min: 30,
    xp: "+120 XP",
    experience_level: "EASY",
    icon: <LayersIcon />,
  },
  {
    id: 9,
    job_name: "Database Query Challenge",
    description: "Write complex SQL queries to extract useful information from a database: joins, groupings, and complex filters.",
    skills: [{ name: "PostgreSQL" }, { name: "Queries" }, { name: "SQL" }],
    test_duration_min: 45,
    xp: "+200 XP",
    experience_level: "MID",
    icon: <DbIcon />,
  },
  {
    id: 10,
    job_name: "Backend API Development",
    description: "Build a REST API with endpoints for managing resources, including full validation, error handling, and basic auth.",
    skills: [{ name: "Node.js" }, { name: "REST API" }, { name: "Backend" }],
    test_duration_min: 70,
    xp: "#380 XP",
    experience_level: "HARD",
    icon: <ApiIcon />,
  },
  {
    id: 11,
    job_name: "Frontend UI Challenge",
    description: "Recreate a modern landing page using HTML and CSS. The result must be responsive and adapts across desktop and mobile screens.",
    skills: [{ name: "Responsive" }, { name: "HTML" }, { name: "CSS" }],
    test_duration_min: 40,
    xp: "+220 XP",
    experience_level: "MID",
    icon: <CodeIcon />,
  },
  {
    id: 12,
    job_name: "System Design Fundamentals Challenge",
    description: "Design the architecture for a scalable web application, including data structures, caching plans, and infrastructure.",
    skills: [{ name: "Architecture" }, { name: "Scalability" }, { name: "Design" }],
    test_duration_min: 80,
    xp: "+450 XP",
    experience_level: "HARD",
    icon: <LayersIcon />,
  }
];

function formatDiff(level) {
  if (!level) return "Medium";
  const normalized = level.toUpperCase();
  if (normalized === "EASY" || normalized === "INTERN" || normalized === "ENTRY") return "Easy";
  if (normalized === "HARD" || normalized === "SENIOR") return "Hard";
  return "Medium";
}

function DiffBadge({ diff }) {
  const displayDiff = formatDiff(diff);
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 4,
      background: displayDiff === "Hard" ? "rgba(239,68,68,0.1)" : displayDiff === "Easy" ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", 
      color: displayDiff === "Hard" ? "#ef4444" : displayDiff === "Easy" ? "#10b981" : "#f59e0b",
      border: `1px solid ${displayDiff === "Hard" ? "rgba(239,68,68,0.2)" : displayDiff === "Easy" ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}`,
      fontFamily: "'Space Mono', monospace"
    }}>{displayDiff}</span>
  );
}

function TestCard({ test, onStart }) {
  const { C } = useTheme();
  const renderIcon = test.icon || <CodeIcon />;
  const completedTests = JSON.parse(localStorage.getItem('completed_tests') || '[]');
  const isCompleted = completedTests.includes(String(test.id));

  return (
    <div 
      onClick={isCompleted ? undefined : onStart}
      style={{
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14,
        position: "relative", transition: "all 0.2s ease", cursor: isCompleted ? "default" : "pointer",
        width: "100%", boxSizing: "border-box",
        opacity: isCompleted ? 0.75 : 1
      }}
      onMouseEnter={e => {
        if (!isCompleted) {
          e.currentTarget.style.borderColor = C.accent;
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = `0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(124, 58, 237, 0.1)`;
        }
      }}
      onMouseLeave={e => {
        if (!isCompleted) {
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: test.color || C.accentDim,
            display: "flex", alignItems: "center", justifyContent: "center", color: test.color ? "#fff" : C.accent
          }}>
            {renderIcon}
          </div>
          {test.company_name && <span style={{ fontSize: 11, color: C.textSecond, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>{test.company_name}</span>}
        </div>
        <DiffBadge diff={test.experience_level} />
      </div>

      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, marginBottom: 8, lineHeight: 1.4 }}>{test.job_name}</h3>
        <p style={{ fontSize: 11.5, color: C.textSecond, lineHeight: 1.6, marginBottom: 12 }}>{test.description}</p>
        
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {test.skills?.map((sk, index) => {
            const skillLabel = typeof sk === 'string' ? sk : sk.name;
            return (
              <span key={index} style={{
                fontSize: 9.5, fontWeight: 500, padding: "2px 8px", borderRadius: 4,
                background: "rgba(59,130,246,0.1)", color: "#93c5fd", border: "1px solid rgba(59,130,246,0.2)"
              }}>{skillLabel}</span>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.textSecond }}>
            <ClockIcon color="currentColor" /> <span>{test.test_duration_min || test.time || 45} min</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: C.accentBright, fontWeight: 600 }}>
            <span>{test.xp || "+200 XP"}</span>
          </div>
        </div>
        
        <button 
          onClick={(e) => {
            e.stopPropagation();
            if (!isCompleted) onStart();
          }}
          disabled={isCompleted}
          style={{
            padding: "6px 16px", borderRadius: 6, border: "none",
            background: isCompleted ? "rgba(16, 185, 129, 0.12)" : C.accentDim, 
            color: isCompleted ? "#10b981" : C.accentBright, 
            fontSize: 11, fontWeight: 600,
            cursor: isCompleted ? "default" : "pointer", transition: "0.18s"
          }}
          onMouseEnter={e => { if (!isCompleted) { e.currentTarget.style.background = C.gradMain; e.currentTarget.style.color = "#fff"; } }}
          onMouseLeave={e => { if (!isCompleted) { e.currentTarget.style.background = C.accentDim; e.currentTarget.style.color = C.accentBright; } }}
        >
          {isCompleted ? "✓ Completed" : "Start Test"}
        </button>
      </div>
    </div>
  );
}

export default function Tests() {
  const { C } = useTheme();
  const navigate = useNavigate(); 
  const { isMobile } = useResponsive(); 
  const [filter, setFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const { onMenuClick } = useOutletContext();
  const [companyTests, setCompanyTests] = useState(DEFAULT_COMPANY_TESTS);
  const [platformTests] = useState(DEFAULT_PLATFORM_TESTS); 

  useEffect(() => {
    const fetchRemoteTests = async () => {
      try {
        // Interrogation du backend via l'instance Axios globale et le bon point de terminaison
        const response = await api.get('/candidates/saved-tests/');
        
        // Extraction sécurisée du tableau 'data' contenu dans la réponse du serveur
        const remoteData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);

        if (remoteData.length > 0) {
          // Fusion des tests du serveur en premier, suivis des mocks locaux de secours
          // On filtre les doublons éventuels basés sur l'ID pour éviter les conflits de clé unique
          const filteredMocks = DEFAULT_COMPANY_TESTS.filter(
            mock => !remoteData.some(remote => String(remote.id) === String(mock.id))
          );
          setCompanyTests([...remoteData, ...filteredMocks]);
        }
      } catch (error) {
        console.log("Serveur déconnecté ou erreur API. Utilisation des fallbacks locaux.", error);
      }
    };

    fetchRemoteTests();
  }, []);

  const filterFn = (t) => {
    const displayDiff = formatDiff(t.experience_level);
    const matchesFilter = filter === "All" || displayDiff === filter;
    
    const matchesSearch = 
      (t.job_name && t.job_name.toLowerCase().includes(searchTerm.toLowerCase())) || 
      (t.company_name && t.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (t.skills && t.skills.some(sk => {
        const name = typeof sk === 'string' ? sk : sk.name;
        return name?.toLowerCase().includes(searchTerm.toLowerCase());
      }));

    return matchesFilter && matchesSearch;
  };

  const filteredCompanyTests = companyTests.filter(filterFn);
  const filteredPlatformTests = platformTests.filter(filterFn);

  const handleStartTest = (testId) => {
    navigate(`/dashboard/test/${testId}`);
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100vh", overflowY: "auto", background: C.bgDeep }}>
      <Topbar title="Tests & Challenges" onMenuClick={onMenuClick}/>
      
      <div style={{ padding: isMobile ? "16px" : "34px 38px", display: "flex", flexDirection: "column", gap: 36, maxWidth: 1200, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        
        {/* Header & Filter */}
        <div>
          <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.textPrimary, marginBottom: 8, letterSpacing: "-0.5px" }}>Tests & Challenges</h1>
          <p style={{ fontSize: 13, color: C.textSecond, marginBottom: 24, maxWidth: 600, lineHeight: 1.5 }}>Complete assessments to earn XP, level up, and get noticed by recruiters.</p>
          
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", gap: 16, marginBottom: 10 }}>
            <div style={{ 
              flex: 1, minWidth: 0, maxWidth: isMobile ? "100%" : 450, position: "relative",
              background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`, borderRadius: 10,
              padding: "0 14px", display: "flex", alignItems: "center", gap: 12, height: 44,
              transition: "border-color 0.2s", boxSizing: "border-box"
            }}
              onFocusCapture={e => e.currentTarget.style.borderColor = C.accent}
              onBlurCapture={e => e.currentTarget.style.borderColor = C.border}
            >
              <SearchIcon color={C.textSecond} size={18} />
              <input 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tests by title or skill..." 
                style={{ 
                  background: "none", border: "none", height: "100%", width: "100%", 
                  fontSize: 13, color: C.textPrimary, outline: "none", marginBottom: "0",
                }} 
              />
            </div>
            
            <div style={{ 
              display: "flex", gap: 4, background: "rgba(255,255,255,0.02)", padding: 4, 
              borderRadius: 10, border: `1px solid ${C.border}`, overflowX: "auto", whiteSpace: "nowrap" 
            }}>
              {["All", "Easy", "Medium", "Hard"].map(f => (
                <button 
                  key={f}
                  onClick={() => setFilter(f)}
                  style={{
                    padding: "7px 16px", borderRadius: 7, border: "none",
                    background: filter === f ? C.accentDim : "none",
                    color: filter === f ? C.accent : C.textSecond,
                    fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
                    flex: isMobile ? 1 : "none"
                  }}
                  onMouseEnter={e => { if (filter !== f) e.currentTarget.style.color = C.textPrimary; }}
                  onMouseLeave={e => { if (filter !== f) e.currentTarget.style.color = C.textSecond; }}
                >{f}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Company Tests Section */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ 
              width: 36, height: 36, borderRadius: 10, background: "rgba(59,130,246,0.1)", 
              display: "flex", alignItems: "center", justifyContent: "center", color: "#3b82f6",
              border: "1px solid rgba(59,130,246,0.15)", flexShrink: 0
            }}>
              <EditIcon />
            </div>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>Company Tests</h2>
              <p style={{ fontSize: 11.5, color: C.textSecond }}>Tests attached to job offers from companies</p>
            </div>
          </div>
          
          {filteredCompanyTests.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
              {filteredCompanyTests.map(test => (
                <TestCard key={test.id} test={test} onStart={() => handleStartTest(test.id)} />
              ))}
            </div>
          ) : (
            <div style={{ padding: "40px 0", textAlign: "center", color: C.textMuted, fontSize: 13 }}>No company tests match your search.</div>
          )}
        </div>

        {/* Platform Tests Section */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <div style={{ 
              width: 36, height: 36, borderRadius: 10, background: "rgba(167,139,250,0.1)", 
              display: "flex", alignItems: "center", justifyContent: "center", color: C.accent,
              border: "1px solid rgba(167,139,250,0.15)", flexShrink: 0
            }}>
              <ShieldIcon />
            </div>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, marginBottom: 2 }}>Platform Tests</h2>
              <p style={{ fontSize: 11.5, color: C.textSecond }}>Tests created by SkillHunt to assess your skills</p>
            </div>
          </div>
          
          {filteredPlatformTests.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
              {filteredPlatformTests.map(test => (
                <TestCard key={test.id} test={test} onStart={() => handleStartTest(test.id)} />
              ))}
            </div>
          ) : (
            <div style={{ padding: "40px 0", textAlign: "center", color: C.textMuted, fontSize: 13 }}>No platform tests match your search.</div>
          )}
        </div>

      </div>
    </div>
  );
}